import {
    useState,
    useEffect,
    useRef,
    RefObject,
    MutableRefObject,
} from "react";
import { Player } from "theoplayer/THEOplayer.chromeless";
import useSaveCaptions from "./useSaveCaptions";
import useCaptions from "./useCaptions";
import {
    Fragment,
    Subtitle,
    VideoActionRequest,
    PlayerButtonType,
} from "core/models";
import { createSrt, createVtt } from "utils/create-subtitle-file";
import saveVttToPc, { generateUrl } from "utils/saveVtt";
import * as localStorageKeys from "core/constants/localStorageKeys";
import useLocalStorage from "ui/hooks/useLocalStorage";

type UploadReturn = {
    updatedFragment: Fragment;
    notificationId: string;
};
interface HookReturn {
    subtitles: Subtitle[];
    language: string;
    videoPlayerRef: React.RefObject<Player | null>;
    activeSubtitle: Subtitle | undefined;
    videoPlayerRequest: VideoActionRequest;
    subtitleTrackSrc: string | undefined;
    pending: boolean;
    uploadPending: boolean;
    error: boolean;
    playing: boolean;
    upload(): Promise<UploadReturn | undefined>;
    handleDownload(type: "vtt" | "srt"): void;
    changeLanguage(lang: string): void;
    handlePlayerButtonClick(buttonType: PlayerButtonType): void;
    setVideoPlayerRequest(request: VideoActionRequest): void;
    timeUpdate(newTime: number): void;
    addSubtitle(index: number, startTime: number): void;
    checkEnoughDistance(index: number): boolean;
    saveSubtitlecardChange(changedSubtitle: Subtitle, subIdx: number): void;
    deleteSubtitle(index: number): void;
    setActiveSubtitle(newActiveSubtitle: Subtitle | undefined): void;
    setPlaying(status: boolean): void;
}

const useSubtitleModal = (
    fragment: Fragment,
    subtitlelistRef: RefObject<HTMLDivElement | null>,
): HookReturn => {
    const [languagePref, setLanguagePref] = useLocalStorage(
        localStorageKeys.CAPTIONS_LANG_PREF,
        "Nl",
    );
    const [language, setLanguage] = useState<string>(
        fragment.getSubtitlesLanguage() || languagePref.toString(),
    );
    const { upload, uploadPending } = useSaveCaptions(); // can also use 'uploadError'
    const [error, setError] = useState<boolean>(false);
    const [subtitleTrackSrc, setSubtitleTrackSrc] = useState<string>();
    const [videoPlayerRequest, setVideoPlayerRequest] =
        useState<VideoActionRequest>({ type: "none" });
    const [videoLoaded, setVideoLoaded] = useState<boolean>(false);
    const [playing, setPlaying] = useState<boolean>(false);
    const videoPlayerRef = useRef<Player | null>(null);
    const {
        subtitles,
        activeSubtitle,
        parsePending,
        setActiveSubtitle,
        addSubtitle,
        changeSubtitleTime,
        deleteSubtitle,
        checkEnoughDistance,
        saveSubtitlecardChange,
    } = useCaptions(
        fragment,
        videoLoaded && videoPlayerRef.current
            ? videoPlayerRef.current.duration
            : 0,
    );

    const pending = parsePending || !videoLoaded;

    const createSubtileTrackSrc = () => {
        if (subtitleTrackSrc) {
            URL.revokeObjectURL(subtitleTrackSrc);
        }
        const vttContent = createVtt(subtitles);
        const vttUrl = generateUrl(vttContent);
        setSubtitleTrackSrc(vttUrl);
    };

    const timeUpdate = (newTime: number): void => {
        if (!videoPlayerRef.current || videoPlayerRef.current.paused) {
            return;
        }
        // Activate subtitle closest to video current time
        const activeCandidates = subtitles.find(
            (s: Subtitle) => newTime >= s.startTime && newTime < s.endTime,
        );
        setActiveSubtitle(activeCandidates);
    };

    useEffect(() => {
        if (subtitles.some((s: Subtitle) => s.error)) {
            setError(true);
        } else {
            setError(false);
        }
    }, [subtitles]);

    useEffect(() => {
        createSubtileTrackSrc();
    }, [subtitles]);

    useEffect(() => {
        // Keeps active subtitle element visible
        if (!subtitlelistRef.current || !activeSubtitle) {
            return;
        }
        const activeIndex = subtitles.findIndex(
            (s: Subtitle) => s.id === activeSubtitle.id,
        );
        const subtitleElement = subtitlelistRef.current.childNodes[
            activeIndex
        ] as HTMLDivElement | undefined;
        subtitleElement?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }, [activeSubtitle, subtitles]);

    const handleLoadedVideoData = (): void => {
        setVideoLoaded(true);
    };

    useEffect(() => {
        if (!videoPlayerRef.current || videoLoaded) {
            return undefined;
        }
        videoPlayerRef.current.addEventListener(
            "loadeddata",
            handleLoadedVideoData,
        );
        return () => {
            videoPlayerRef.current?.removeEventListener(
                "loadeddata",
                handleLoadedVideoData,
            );
        };
    });

    // Action handling
    const changeLanguage = (lang: string): void => {
        setLanguage(lang);
        setLanguagePref(lang);
    };
    const handleDownload = (type: "vtt" | "srt") => {
        const content =
            type === "vtt" ? createVtt(subtitles) : createSrt(subtitles);
        saveVttToPc(content, `${fragment.title}.${type}`);
    };
    const handlePlayerButtonClick = (buttonType: PlayerButtonType): void => {
        switch (buttonType) {
            case "togglePlay":
                setVideoPlayerRequest({ type: "togglePlay" });
                break;
            case "goToInPoint":
                setVideoPlayerRequest({
                    type: "setTime",
                    value: activeSubtitle?.startTime,
                });
                break;
            case "goToOutPoint":
                setVideoPlayerRequest({
                    type: "setTime",
                    value: activeSubtitle?.endTime,
                });
                break;
            case "playFromStart":
                setVideoPlayerRequest({ type: "playFromStart" });
                break;
            case "moveFrameBackward":
                setVideoPlayerRequest({ type: "frameBackwards" });
                break;
            case "moveFrameForward":
                setVideoPlayerRequest({ type: "frameForwards" });
                break;
            case "setInPoint":
                if (activeSubtitle) {
                    if (videoPlayerRef.current) {
                        changeSubtitleTime(
                            "startTime",
                            videoPlayerRef.current.currentTime,
                        );
                    }
                    return;
                }
                if (videoPlayerRef.current) {
                    addSubtitle(0, videoPlayerRef.current.currentTime);
                }
                break;
            case "setOutPoint":
                if (videoPlayerRef.current) {
                    changeSubtitleTime(
                        "endTime",
                        videoPlayerRef.current.currentTime,
                    );
                }
                break;
            case "playLast2Seconds":
                setVideoPlayerRequest({ type: "playLastSeconds", value: 2 });
                break;
            default:
                break;
        }
    };

    return {
        subtitles,
        language,
        videoPlayerRef,
        activeSubtitle,
        videoPlayerRequest,
        subtitleTrackSrc,
        pending,
        uploadPending,
        error,
        playing,
        upload: async () => {
            if (!subtitleTrackSrc) {
                return undefined;
            }
            return upload(fragment, language, subtitleTrackSrc);
        },
        handleDownload,
        changeLanguage,
        handlePlayerButtonClick,
        setVideoPlayerRequest,
        timeUpdate,
        addSubtitle,
        checkEnoughDistance,
        saveSubtitlecardChange,
        deleteSubtitle,
        setActiveSubtitle,
        setPlaying: (status: boolean) => setPlaying(status),
    };
};
export default useSubtitleModal;
